import { Formik } from 'formik';
import { Alert, Button, Card, CardBody, CardFooter, Checkbox, Heading, IconWarning } from '../../ui';
import * as yup from 'yup';

const validationSchema = yup.object({
    gdprConsent: yup.bool().required('Must be checked').oneOf([true], 'Must be checked'),
    treatmentConsent: yup.bool().required('Must be checked').oneOf([true], 'Must be checked'),
    abilityConfirmation: yup.bool().required('Must be checked').oneOf([true], 'Must be checked'),
});

export interface CamhsConsentFormValues {
    gdprConsent: boolean;
    treatmentConsent: boolean;
    abilityConfirmation: boolean;
}

const defaultValues = {
    gdprConsent: false,
    treatmentConsent: false,
    abilityConfirmation: false,
};

export interface CamhsConsentProps {
    initialValues?: CamhsConsentFormValues;
    childName: string;
    onSubmit: (values: CamhsConsentFormValues) => Promise<void> | void;
}

export function CamhsConsent({ initialValues, childName, onSubmit }: CamhsConsentProps) {
    return (
        <div className="max-w-[684px] mx-auto my-20 space-y-6">
            <div className="text-center space-y-6">
                <Heading level={1}>Consent to {childName}'s' treatment</Heading>
                <p>
                    {childName}’s wellbeing is our top priority. By consenting below, you allow us to support their
                    growth in a safe and secure environment.
                </p>
            </div>

            <Formik
                initialValues={initialValues || defaultValues}
                onSubmit={onSubmit}
                validationSchema={validationSchema}
            >
                {({ handleChange, isSubmitting, errors, handleSubmit, values, submitCount }) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <Card>
                            <CardBody>
                                <div className="space-y-6">
                                    <Checkbox
                                        name="gdprConsent"
                                        checked={values.gdprConsent}
                                        onChange={handleChange}
                                        hideError
                                    >
                                        I consent to the{' '}
                                        <a
                                            href="https://www.helloself.com/privacy-promise"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            processing of data
                                        </a>{' '}
                                        regarding {childName}'s care under GDPR.{' '}
                                    </Checkbox>
                                    <Checkbox
                                        name="treatmentConsent"
                                        checked={values.treatmentConsent}
                                        onChange={handleChange}
                                        hideError
                                    >
                                        I consent for {childName} to be assessed and treated by HelloSelf
                                    </Checkbox>
                                    <Checkbox
                                        name="abilityConfirmation"
                                        checked={values.abilityConfirmation}
                                        onChange={handleChange}
                                        hideError
                                    >
                                        I confirm that {childName} has the intellectual and IT ability to engage in
                                        remote therapy and to use HelloSelf's platform technological features
                                    </Checkbox>
                                    <p>
                                        By accepting these{' '}
                                        <a
                                            href="https://www.helloself.com/terms-and-conditions"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            terms and conditions
                                        </a>
                                        , I confirm that all legal guardians of {childName} are aware and consent to{' '}
                                        {childName}'s treatment. Please contact us, if you have questions.
                                    </p>
                                    {Object.keys(errors).length && submitCount > 0 ? (
                                        <Alert type="error">
                                            <div className="flex items-start space-x-2 gap-2">
                                                <div>
                                                    <IconWarning />
                                                </div>
                                                <span>
                                                    We need your full consent to your child's treatment before we can
                                                    proceed. If you have any questions or concerns, please contact us at{' '}
                                                    <a href="mailto:hello@helloself.com">hello@helloself.com</a>.
                                                </span>
                                            </div>
                                        </Alert>
                                    ) : null}
                                </div>
                            </CardBody>
                            <CardFooter>
                                <div className="flex justify-end items-center">
                                    <Button isProcessing={isSubmitting} type="submit" disabled={isSubmitting}>
                                        Continue
                                    </Button>
                                </div>
                            </CardFooter>
                        </Card>
                    </form>
                )}
            </Formik>
        </div>
    );
}
